import type { Virtualizer } from '@tanstack/react-virtual';
import { add, differenceInCalendarDays, startOfDay } from 'date-fns';
import { useCallback, useEffect, useRef } from 'react';
import { CalendarEventOrganiser } from '../CalendarEventOrganiser';
import type { CalendarEvent } from './usePlannerEvents';

// Constants
const EXTENSION_THRESHOLD_DAYS = 30;
const EXTENSION_THRESHOLD_PIXELS_MULTIPLIER = 30;
const OUTLET_WIDTH = 414;
const SCROLL_MARGIN = 50;

interface UsePlannerScrollOptions {
  /** Parent scroll container ref */
  parentRef: React.RefObject<HTMLDivElement>;
  /** Column virtualizer instance */
  columnVirtualizer: Virtualizer<HTMLDivElement, Element>;
  /** Whether on mobile device */
  isMobile: boolean;
  /** Column width in pixels */
  columnWidth: number;
  /** Start date of the planner range */
  start: Date;
  /** Total column count */
  count: number;
  /** Current date range end (managed by parent) */
  dateRangeEnd: Date;
  /** Setter for date range end (for infinite scroll extension) */
  setDateRangeEnd: React.Dispatch<React.SetStateAction<Date>>;
  /** Sidebar width (desktop) */
  sideBarWidth: number;
  /** Collapsed mobile sidebar width */
  collapsedMobileSideBarWidth: number;
  /** Events array for finding jobs */
  events: CalendarEvent[];
  /** Callback when visible date changes */
  onVisibleDateChange?: (visibleDate: Date) => void;
}

interface UsePlannerScrollResult {
  /** Scroll to a specific date */
  scrollToDate: (date: Date, paddingLeftDays: number, smooth: boolean) => void;
  /** Scroll to reveal a job or date (desktop only) */
  scrollToRevealJob: (
    jobIdOrDate: string | Date,
    siteId?: string,
    isJobCreationScroll?: boolean
  ) => void;
  /** Handler for Today button click */
  handleTodayClick: () => void;
  /** Ref to track if we've scrolled to reveal for job creation */
  hasScrolledToRevealForCreationRef: React.MutableRefObject<boolean>;
  /** Ref to track if programmatic scroll is in progress */
  isProgrammaticScrollRef: React.MutableRefObject<boolean>;
}

/**
 * Handles all scroll-related functionality for the planner:
 * - scrollToDate: Navigate to a specific date
 * - scrollToRevealJob: Reveal a job/date hidden behind outlet
 * - Date range extension (infinite scroll)
 * - Visible date change tracking
 *
 * This hook creates isProgrammaticScrollRef which should be passed to
 * usePlannerInitialization for coordination.
 */
export function usePlannerScroll({
  parentRef,
  columnVirtualizer,
  isMobile,
  columnWidth,
  start,
  count,
  dateRangeEnd,
  setDateRangeEnd,
  sideBarWidth,
  collapsedMobileSideBarWidth,
  events,
  onVisibleDateChange,
}: UsePlannerScrollOptions): UsePlannerScrollResult {
  // Refs for date range extension
  const lastExtendedEndRef = useRef<Date | null>(null);
  const isExtendingRef = useRef(false);
  const dateRangeEndRef = useRef(dateRangeEnd);
  const extensionRafRef = useRef<number | null>(null);
  const lastNotifiedVisibleDateRef = useRef<Date | null>(null);

  // Track if we've already scrolled to reveal for the current job creation session
  const hasScrolledToRevealForCreationRef = useRef(false);

  // Track if programmatic scroll is in progress (shared with initialization hook)
  const isProgrammaticScrollRef = useRef(false);

  // Keep dateRangeEndRef in sync with state
  useEffect(() => {
    dateRangeEndRef.current = dateRangeEnd;
  }, [dateRangeEnd]);

  // Scroll to a specific date
  // NOTE: columnVirtualizer is excluded from deps - it's a stable object returned by useVirtualizer.
  const scrollToDate = useCallback(
    (date: Date, _paddingLeftDays: number, smooth: boolean, retryCount = 0) => {
      const targetDate = startOfDay(date);
      let dayIndex = differenceInCalendarDays(targetDate, start);

      // Clamp to valid range
      dayIndex = Math.max(0, Math.min(dayIndex, count - 1));

      // Ensure virtualizer has valid range before scrolling
      if (!columnVirtualizer.range) {
        // Retry up to 3 times with RAF
        if (retryCount < 3) {
          requestAnimationFrame(() => {
            scrollToDate(date, _paddingLeftDays, smooth, retryCount + 1);
          });
          return;
        }

        return;
      }

      // On mobile/tablet, ensure today appears as the FIRST visible column
      if (isMobile) {
        const targetScrollOffset = Math.max(0, dayIndex * columnWidth);

        columnVirtualizer.scrollToOffset(targetScrollOffset, {
          behavior: smooth ? 'smooth' : 'auto',
        });
      } else {
        // Desktop: use default behavior with scrollPaddingStart
        columnVirtualizer.scrollToIndex(dayIndex, {
          align: 'start',
          behavior: smooth ? 'smooth' : 'auto',
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [start, count, isMobile, columnWidth]
  );

  // Handler for Today button click
  const handleTodayClick = useCallback(() => {
    scrollToDate(new Date(), 0, true);
  }, [scrollToDate]);

  // Desktop-only scroll to reveal (for both jobs and dates)
  // NOTE: columnVirtualizer is excluded from deps - it's a stable object returned by useVirtualizer.
  const scrollToRevealJob = useCallback(
    (
      jobIdOrDate: string | Date,
      _siteId?: string,
      isJobCreationScroll = false
    ) => {
      if (isMobile) return;

      // Block job creation scrolls if we've already scrolled once for this session
      if (isJobCreationScroll && hasScrolledToRevealForCreationRef.current) {
        return;
      }

      let columnIndex: number;

      if (typeof jobIdOrDate === 'string') {
        // Handle job ID - find the job in events
        const jobEvent = events.find(({ event }) => event.id === jobIdOrDate);
        if (!jobEvent) return;

        const { x: jobColumnIndex, event } = jobEvent;
        const jobDays = CalendarEventOrganiser.getCalendarEventLength(event);
        columnIndex = jobColumnIndex + jobDays - 1;
      } else {
        // Handle date - calculate the column index
        columnIndex = differenceInCalendarDays(startOfDay(jobIdOrDate), start);
        if (columnIndex < 0) return;
      }

      // Calculate if the target is hidden behind outlet
      const rightEdge = (columnIndex + 1) * columnWidth;
      const currentScrollOffset = columnVirtualizer.scrollOffset;
      if (currentScrollOffset === null) return;

      const rightEdgeInViewport =
        rightEdge -
        currentScrollOffset +
        (isMobile ? collapsedMobileSideBarWidth : sideBarWidth);

      // Only scroll if target is actually hidden
      if (
        window.innerWidth - rightEdgeInViewport <
        OUTLET_WIDTH + SCROLL_MARGIN
      ) {
        const scrollNeeded =
          OUTLET_WIDTH +
          SCROLL_MARGIN -
          (window.innerWidth - rightEdgeInViewport);
        columnVirtualizer.scrollToOffset(currentScrollOffset + scrollNeeded, {
          behavior: 'smooth',
        });
      }

      // Mark that we've scrolled for job creation
      if (isJobCreationScroll) {
        hasScrolledToRevealForCreationRef.current = true;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      events,
      start,
      columnWidth,
      isMobile,
      sideBarWidth,
      collapsedMobileSideBarWidth,
    ]
  );

  // Date range extension and visible date tracking effect
  useEffect(() => {
    const scrollElement = parentRef.current;
    if (!scrollElement) return;

    const handleScroll = () => {
      // Prevent multiple simultaneous extensions
      if (isExtendingRef.current) return;

      const currentRange = columnVirtualizer.range;
      if (!currentRange) return;

      const { endIndex } = currentRange;
      const currentCount = differenceInCalendarDays(
        dateRangeEndRef.current,
        start
      );
      const daysFromEnd = currentCount - endIndex;

      // Check pixel-based distance as fallback
      const totalSize = columnVirtualizer.getTotalSize();
      const scrollLeft = scrollElement.scrollLeft;
      const clientWidth = scrollElement.clientWidth;
      const distanceFromEnd = totalSize - scrollLeft - clientWidth;
      const thresholdPixels =
        columnWidth * EXTENSION_THRESHOLD_PIXELS_MULTIPLIER;

      const shouldExtend =
        daysFromEnd <= EXTENSION_THRESHOLD_DAYS ||
        (distanceFromEnd <= thresholdPixels && totalSize > 0);

      if (shouldExtend) {
        const currentEnd = dateRangeEndRef.current;
        const lastExtendedEnd = lastExtendedEndRef.current;

        if (!lastExtendedEnd || currentEnd >= lastExtendedEnd) {
          isExtendingRef.current = true;

          const newEnd = add(currentEnd, { years: 1 });
          lastExtendedEndRef.current = newEnd;

          setDateRangeEnd((prevEnd) => {
            if (prevEnd >= newEnd) {
              return prevEnd;
            }
            dateRangeEndRef.current = newEnd;
            return newEnd;
          });

          if (extensionRafRef.current !== null) {
            cancelAnimationFrame(extensionRafRef.current);
          }

          extensionRafRef.current = requestAnimationFrame(() => {
            isExtendingRef.current = false;
            extensionRafRef.current = null;
          });
        }
      }

      // Track visible date changes for navigation
      // Skip during programmatic scrolls to avoid interference
      if (
        onVisibleDateChange &&
        currentRange &&
        !isProgrammaticScrollRef.current
      ) {
        const { startIndex, endIndex: rangeEndIndex } = currentRange;
        const centerIndex = Math.floor((startIndex + rangeEndIndex) / 2);
        const centerDate = startOfDay(add(start, { days: centerIndex }));

        const lastNotified = lastNotifiedVisibleDateRef.current;
        if (!lastNotified || centerDate.getTime() !== lastNotified.getTime()) {
          lastNotifiedVisibleDateRef.current = centerDate;
          onVisibleDateChange(centerDate);
        }
      }
    };

    // Throttle scroll events with RAF
    let rafId: number | null = null;
    const throttledHandleScroll = () => {
      if (rafId === null) {
        rafId = requestAnimationFrame(() => {
          handleScroll();
          rafId = null;
        });
      }
    };

    scrollElement.addEventListener('scroll', throttledHandleScroll, {
      passive: true,
    });

    // Check initial scroll position
    const initialCheckTimeout = setTimeout(() => {
      throttledHandleScroll();
    }, 100);

    return () => {
      clearTimeout(initialCheckTimeout);
      if (extensionRafRef.current !== null) {
        cancelAnimationFrame(extensionRafRef.current);
        extensionRafRef.current = null;
      }
      scrollElement.removeEventListener('scroll', throttledHandleScroll);
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
    };
    // columnVirtualizer is excluded from deps - it's a stable object returned by useVirtualizer.
    // Including it would cause infinite re-runs since the object reference changes on every render,
    // but the methods we call (range, getTotalSize, scrollToOffset) are stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnWidth, start, onVisibleDateChange, parentRef, setDateRangeEnd]);

  return {
    scrollToDate,
    scrollToRevealJob,
    handleTodayClick,
    hasScrolledToRevealForCreationRef,
    isProgrammaticScrollRef,
  };
}
